import { useState } from 'react';
import { motion } from 'framer-motion';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Plus, 
  Search,
  Users as UsersIcon,
  ShieldCheck,
  Building2,
  MoreVertical,
  Edit,
  Trash2,
  Mail,
  Eye as EyeIcon,
  Loader2,
  AlertCircle,
  RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { usersService, companiesService } from '@/services';
import type { ApiUser, CreateUserRequest, UpdateUserRequest } from '@/types/api';
import { cn } from '@/lib/utils';
import { UserFormDialog } from '@/components/users/UserFormDialog';
import { UserDeleteDialog } from '@/components/users/UserDeleteDialog';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';

const roleLabels = {
  superadmin: 'Super Admin',
  company_admin: 'Admin de Empresa',
};

const roleIcons = {
  superadmin: ShieldCheck,
  company_admin: Building2,
};

const roleColors = {
  superadmin: 'bg-destructive/10 text-destructive border-destructive/20',
  company_admin: 'bg-primary/10 text-primary border-primary/20',
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

// Skeleton component
function UserCardSkeleton() {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-4">
          <Skeleton className="h-12 w-12 rounded-full" />
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <Skeleton className="h-5 w-32" />
              <Skeleton className="h-5 w-24" />
            </div>
            <div className="flex items-center gap-4">
              <Skeleton className="h-4 w-40" />
              <Skeleton className="h-4 w-32" />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function Users() {
  const [searchTerm, setSearchTerm] = useState('');
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<ApiUser | undefined>();
  const [userToDelete, setUserToDelete] = useState<ApiUser | null>(null);
  const { toast } = useToast();
  const { isSuperAdmin, currentUser } = useAuth();
  const queryClient = useQueryClient();

  // Query para obtener usuarios
  const { 
    data: usersData, 
    isLoading: usersLoading, 
    error: usersError,
    refetch 
  } = useQuery({
    queryKey: ['users', searchTerm],
    queryFn: async () => {
      const response = await usersService.getAll({ 
        search: searchTerm || undefined,
        pageSize: 100 
      });
      if (!response.success) {
        throw new Error(response.error?.message || 'Error al cargar usuarios');
      }
      return response.data;
    },
  });

  // Query para obtener empresas (para mostrar nombres)
  const { data: companiesData } = useQuery({
    queryKey: ['companies'],
    queryFn: async () => {
      const response = await companiesService.getAll({ pageSize: 100 });
      if (!response.success) {
        throw new Error(response.error?.message || 'Error al cargar empresas');
      }
      return response.data;
    },
  });

  // Mutation para crear usuario
  const createMutation = useMutation({
    mutationFn: (data: CreateUserRequest) => usersService.create(data),
    onSuccess: (response) => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: ['users'] });
        toast({
          title: 'Usuario creado',
          description: 'El usuario ha sido creado correctamente.',
        });
        setFormDialogOpen(false);
      } else {
        toast({
          title: 'Error',
          description: response.error?.message || 'Error al crear el usuario',
          variant: 'destructive',
        });
      }
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Mutation para actualizar usuario
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateUserRequest }) => 
      usersService.update(id, data),
    onSuccess: (response) => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: ['users'] });
        toast({
          title: 'Usuario actualizado',
          description: 'El usuario ha sido actualizado correctamente.',
        });
        setFormDialogOpen(false);
      } else {
        toast({
          title: 'Error',
          description: response.error?.message || 'Error al actualizar el usuario',
          variant: 'destructive',
        });
      }
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Mutation para eliminar usuario
  const deleteMutation = useMutation({
    mutationFn: (id: string) => usersService.delete(id),
    onSuccess: (response) => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: ['users'] });
        toast({
          title: 'Usuario eliminado',
          description: 'El usuario ha sido eliminado.',
          variant: 'destructive',
        });
        setDeleteDialogOpen(false);
        setUserToDelete(null);
      } else {
        toast({
          title: 'Error',
          description: response.error?.message || 'Error al eliminar el usuario',
          variant: 'destructive',
        });
      }
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const users = usersData?.items || [];
  const companies = companiesData?.items || [];

  const getCompanyName = (companyId?: string | null) => {
    if (!companyId) return 'Sin empresa';
    return companies.find(c => c.id === companyId)?.name || 'Sin empresa';
  };

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const handleNewUser = () => {
    setSelectedUser(undefined);
    setFormDialogOpen(true);
  };

  const handleEditUser = (user: ApiUser) => {
    setSelectedUser(user);
    setFormDialogOpen(true);
  };

  const handleDeleteClick = (user: ApiUser) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const [isUpdatingUser, setIsUpdatingUser] = useState(false);

  const handleFormSubmit = async (data: { name: string; email: string; password?: string; role: 'superadmin' | 'company_admin'; companyId?: string }) => {
    if (selectedUser) {
      setIsUpdatingUser(true);
      try {
        // Actualizar datos del usuario
        const updateResponse = await usersService.update(selectedUser.id, {
          name: data.name,
          email: data.email,
          role: data.role,
          companyId: data.companyId,
        });

        if (!updateResponse.success) {
          toast({
            title: 'Error',
            description: updateResponse.error?.message || 'Error al actualizar el usuario',
            variant: 'destructive',
          });
          return;
        }

        // Si se proporcionó una contraseña, actualizarla por separado usando el endpoint específico
        if (data.password && data.password.trim() !== '') {
          const passwordResponse = await usersService.updatePassword(selectedUser.id, {
            password: data.password,
          });

          if (!passwordResponse.success) {
            toast({
              title: 'Usuario actualizado',
              description: 'Los datos se actualizaron, pero hubo un error al cambiar la contraseña: ' + (passwordResponse.error?.message || 'Error desconocido'),
              variant: 'destructive',
            });
            return;
          }
        }

        // Éxito en ambas operaciones
        queryClient.invalidateQueries({ queryKey: ['users'] });
        toast({
          title: 'Usuario actualizado',
          description: data.password && data.password.trim() !== '' 
            ? 'El usuario y su contraseña han sido actualizados correctamente.'
            : 'El usuario ha sido actualizado correctamente.',
        });
        setFormDialogOpen(false);
        setSelectedUser(undefined);
      } catch (error) {
        toast({
          title: 'Error',
          description: error instanceof Error ? error.message : 'Error inesperado al actualizar el usuario',
          variant: 'destructive',
        });
      } finally {
        setIsUpdatingUser(false);
      }
    } else {
      createMutation.mutate({
        name: data.name,
        email: data.email,
        password: data.password!,
        role: data.role,
        companyId: data.companyId,
      });
    }
  };

  const handleDeleteConfirm = () => {
    if (userToDelete) {
      deleteMutation.mutate(userToDelete.id);
    }
  };

  const canEditUser = (user: ApiUser) => {
    if (isSuperAdmin) return true;
    if (user.role === 'superadmin') return false;
    return currentUser?.companyId === user.companyId;
  };

  const canDeleteUser = (user: ApiUser) => {
    if (currentUser?.id === user.id) return false;
    return canEditUser(user);
  };

  // Count by role
  const superadminCount = users.filter(u => u.role === 'superadmin').length;
  const companyAdminCount = users.filter(u => u.role === 'company_admin').length;

  // Loading state
  if (usersLoading) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-display font-bold text-foreground">Usuarios</h1>
            <p className="text-muted-foreground mt-1">Gestiona los usuarios y sus permisos</p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Skeleton className="h-20" />
          <Skeleton className="h-20" />
        </div>
        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map((i) => (
            <UserCardSkeleton key={i} />
          ))}
        </div>
      </div>
    );
  }

  // Error state
  if (usersError) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-display font-bold text-foreground">Usuarios</h1>
            <p className="text-muted-foreground mt-1">Gestiona los usuarios y sus permisos</p>
          </div>
        </div>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="flex items-center justify-between">
            <span>{usersError instanceof Error ? usersError.message : 'Error al cargar usuarios'}</span>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Reintentar
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-display font-bold text-foreground">
            Usuarios
          </h1>
          <p className="text-muted-foreground mt-1">
            Gestiona los usuarios y sus permisos
          </p>
        </div>
        {isSuperAdmin && (
          <Button 
            onClick={handleNewUser}
            className="gradient-primary text-white shadow-md hover:shadow-lg transition-shadow"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Usuario
          </Button>
        )}
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Buscar usuarios..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Summary by role */}
      <div className="grid grid-cols-2 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className={cn('p-2 rounded-lg', roleColors.superadmin.split(' ')[0])}>
              <ShieldCheck className={cn('w-5 h-5', roleColors.superadmin.split(' ')[1])} />
            </div>
            <div>
              <p className="text-2xl font-display font-bold">{superadminCount}</p>
              <p className="text-xs text-muted-foreground">{roleLabels.superadmin}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className={cn('p-2 rounded-lg', roleColors.company_admin.split(' ')[0])}>
              <Building2 className={cn('w-5 h-5', roleColors.company_admin.split(' ')[1])} />
            </div>
            <div>
              <p className="text-2xl font-display font-bold">{companyAdminCount}</p>
              <p className="text-xs text-muted-foreground">{roleLabels.company_admin}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Users list */}
      <div className="space-y-3">
        {users.map((user) => {
          const RoleIcon = roleIcons[user.role];
          
          return (
            <motion.div key={user.id} variants={itemVariants}>
              <Card className="hover:shadow-card-hover transition-all duration-300">
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    {/* Avatar */}
                    <Avatar className="h-12 w-12">
                      <AvatarFallback className="bg-primary text-primary-foreground font-medium">
                        {getInitials(user.name)}
                      </AvatarFallback>
                    </Avatar>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-foreground">
                          {user.name}
                        </h3>
                        <Badge 
                          variant="outline" 
                          className={cn('gap-1', roleColors[user.role])}
                        >
                          <RoleIcon className="w-3 h-3" />
                          {roleLabels[user.role]}
                        </Badge>
                        {currentUser?.id === user.id && (
                          <Badge variant="secondary" className="text-xs">
                            Tú
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <Mail className="w-3.5 h-3.5" />
                          <span className="truncate">{user.email}</span>
                        </div>
                        {user.role === 'company_admin' && (
                          <>
                            <span>•</span>
                            <span>{getCompanyName(user.companyId)}</span>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem>
                          <EyeIcon className="w-4 h-4 mr-2" />
                          Ver perfil
                        </DropdownMenuItem>
                        {canEditUser(user) && (
                          <DropdownMenuItem onClick={() => handleEditUser(user)}>
                            <Edit className="w-4 h-4 mr-2" />
                            Editar
                          </DropdownMenuItem>
                        )}
                        {canDeleteUser(user) && (
                          <DropdownMenuItem 
                            onClick={() => handleDeleteClick(user)}
                            className="text-destructive focus:text-destructive"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Eliminar
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          );
        })}
      </div>

      {users.length === 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center py-12"
        >
          <UsersIcon className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">
            No se encontraron usuarios.
          </p>
        </motion.div>
      )}

      {/* Dialogs */}
      <UserFormDialog
        open={formDialogOpen}
        onOpenChange={setFormDialogOpen}
        user={selectedUser ? {
          id: selectedUser.id,
          email: selectedUser.email,
          name: selectedUser.name,
          role: selectedUser.role,
          companyId: selectedUser.companyId || undefined,
          createdAt: new Date(selectedUser.createdAt),
          updatedAt: new Date(selectedUser.updatedAt),
        } : undefined}
        companies={companies.map(c => ({
          id: c.id,
          name: c.name,
          cif: c.cif,
          address: c.address,
          phone: c.phone,
          email: c.email,
          createdAt: new Date(c.createdAt),
          updatedAt: new Date(c.updatedAt),
        }))}
        onSubmit={handleFormSubmit}
        isLoading={createMutation.isPending || isUpdatingUser}
      />

      <UserDeleteDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        user={userToDelete ? {
          id: userToDelete.id,
          email: userToDelete.email,
          name: userToDelete.name,
          role: userToDelete.role,
          companyId: userToDelete.companyId || undefined,
          createdAt: new Date(userToDelete.createdAt),
          updatedAt: new Date(userToDelete.updatedAt),
        } : null}
        onConfirm={handleDeleteConfirm}
        isLoading={deleteMutation.isPending}
      />
    </motion.div>
  );
}
